import { HTMLAttributes, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import axios from "axios"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { useMutation } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { Link, useNavigate } from "react-router-dom"
import {
  ACCEPT_INVITE_ENDPOINT,
  NEW_USERS_INVITATION_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"
import { toast } from "@hooks/use-toast"
import { LOGIN_PAGE_ROUTE } from "@pma/utils/page-routes"
import { PasswordInput } from "@components/common/password-input"
import { cn } from "@pma/utils/helpers"
import { Card } from "@components/common/card"
import Recaptcha from "./recaptcha"
import useTranslator from "@hooks/use-translator"
import { REGEX_PHONE_NUMBER } from "@pma/utils/constants"
import GoogleSignIn from "./google-sign-in"

interface InvitationFormProps extends HTMLAttributes<HTMLDivElement> {
  inviteKey: string
}

const formSchema = z
  .object({
    key: z.string(),
    name: z.string().min(1, { message: "Please enter your name" }),
    phone: z
      .string()
      .min(1, { message: "Invalid phone number" })
      .regex(REGEX_PHONE_NUMBER, "Invalid Number!"),
    password: z
      .string()
      .min(1, {
        message: "Please enter your password",
      })
      .min(6, {
        message: "Password must be at least 6 characters long",
      }),
    confirm_password: z.string().min(6, {
      message: "Please confirm your password",
    }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Passwords don't match.",
    path: ["confirm_password"],
  })

function InvitationForm({ className, inviteKey, ...props }: InvitationFormProps) {
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  })
  const [v2Fallback, setV2Fallback] = useState(false)

  const navigate = useNavigate()
  const { translate } = useTranslator()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      key: inviteKey,
    },
  })

  const newUserInvite = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      })
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return
    }

    const response = await axios.post(SERVER_URL + NEW_USERS_INVITATION_ENDPOINT, {
      ...payload,
      recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
      recaptcha_response_v2: payload?.v2Token,
    })

    return response.data
  }

  const newUserInvitationMutation = useMutation({
    mutationKey: ["new-user-invitation"],
    mutationFn: (payload: DictType) => newUserInvite(payload),
    onSuccess: (data) => {
      navigate(LOGIN_PAGE_ROUTE)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "Invalid Captcha Response. Please try again.") {
        setV2Fallback(true)
      } else {
        toast({
          title: translate("Error"),
          description: errorMessage || translate("Unable to accept invite at this time"),
          variant: "error",
        })
      }
    },
  })

  function onSubmit(data: z.infer<typeof formSchema>) {
    newUserInvitationMutation.mutate(data)
  }

  const reSubmit = (token: string) => {
    newUserInvitationMutation.mutate({ v2Token: token })
  }

  return (
    <Card
      className='h-[500px] w-full rounded-md border-none p-6 md:w-[350px]'
      style={{
        background: "linear-gradient(blue 5.01%, rgb(232, 203, 203, 0%)",
        borderRadius: "15px 15px 0px 0px",
      }}
    >
      <div className='flex flex-col items-center justify-center space-y-2 text-left'>
        <div className='relative mt-0 p-0'>
          <img src={`${process.env.PUBLIC_URL}/group-232.svg`} alt='Brand logo' />
        </div>
      </div>

      <div className={cn("grid gap-6", className)} {...props}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='text-white'>
            <div className='flex flex-col gap-4'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='flex flex-col items-start justify-start space-y-2'>
                    <FormLabel className='capitalize'>{translate("Name")}</FormLabel>
                    <FormControl>
                      <Input placeholder={"name"} className='rounded-full text-black' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='phone'
                render={({ field }) => (
                  <FormItem className='flex flex-col items-start justify-start space-y-2'>
                    <FormLabel className='capitalize'>{translate("Phone Number")}</FormLabel>
                    <FormControl>
                      <Input placeholder={"+88"} className='rounded-full text-black' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem className='flex w-full flex-col space-y-2'>
                    <FormLabel className='w-full capitalize ltr:text-left rtl:text-right'>
                      {translate("Password")}
                    </FormLabel>

                    <FormControl className=''>
                      <PasswordInput
                        placeholder='********'
                        {...field}
                        className='w-full rounded-full bg-white text-black'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='confirm_password'
                render={({ field }) => (
                  <FormItem className='flex w-full flex-col space-y-2'>
                    <FormLabel className='w-full capitalize ltr:text-left rtl:text-right'>
                      {translate("Confirm Password")}
                    </FormLabel>

                    <FormControl className=''>
                      <PasswordInput
                        placeholder='********'
                        autoComplete='new-password'
                        {...field}
                        className='w-full rounded-full bg-white text-black'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                className='mt-2 rounded-full bg-primary/80 capitalize'
                loading={newUserInvitationMutation.isPending}
                disabled={
                  newUserInvitationMutation.isPending || newUserInvitationMutation.isSuccess
                }
              >
                {translate("Create account")}
              </Button>
            </div>
            <div className='relative my-2'>
              <div className='absolute inset-0 flex items-center'>
                <span className='w-full border-t' />
              </div>
              <div className='relative flex justify-center text-xs uppercase'>
                <span className='bg-background px-2 text-foreground'>
                  {translate("or continue with")}{" "}
                </span>
              </div>
            </div>

            <div className='flex items-center gap-2'>
              <GoogleSignIn />
            </div>
          </form>
        </Form>
      </div>
      <div className='my-3 flex items-center justify-end gap-2 text-white/60'>
        <span className='text-sm font-medium text-white/60 hover:opacity-75'>
          {translate("Already have an account")}?
        </span>
        <Link to={LOGIN_PAGE_ROUTE} className='text-sm font-medium text-white hover:opacity-75'>
          {translate("Log in instead")}
        </Link>
      </div>

      {/* GOOGLE Recaptcha */}
      <Recaptcha setToken={setRecaptchaToken} v2={v2Fallback} reSubmit={reSubmit} />
    </Card>
  )
}

export default InvitationForm
